import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaPlay, FaYoutube, FaArrowRight, FaBookOpen, FaHandHoldingUsd,
  FaSeedling, FaFilePdf, FaBookReader, FaRegLightbulb, FaQuoteLeft,
} from 'react-icons/fa';
import './Financial.css';
import Footer from '../Footer';
import pastorJudithImg from '../../Assets/PastorJudith.jpg';
import bAgumaImg from '../../Assets/bAguma.jpeg';
import trainingImg from '../../Assets/training.jpg';
import drAlexImg from '../../Assets/PastorA.jpeg';

/* Ministry coordinator. Bio copy is intentionally role-based;
   replace with her own words when available. */
const VISION = {
  name: 'Dr Alex Mutagubya',
  role: 'Vision Bearer',
  image: drAlexImg,
};

const ROWENA = {
  name: 'Rowena Turinawe',
  role: 'Coordinator, Money Matters',
  image: null,
  intro:
    'Rowena Turinawe coordinates the Money Matters ministry with warmth, clarity and conviction, helping our church family grow in financial wisdom that is rooted in Scripture and lived out in everyday life.',
  bio:
    'From budgeting and saving to generosity and freedom from debt, the ministry makes biblical stewardship practical and hopeful. Whether you are just starting out or rebuilding, Money Matters is a place to learn, ask honest questions, and take confident next steps toward financial peace.',
  quote: 'Money is a tool for ministry, family and freedom, when we steward it faithfully.',
};

/* Unique featured teachings. (Provided links #1 and #2 were the same video,
   deduped so the same teaching isn't embedded twice.) */
const VIDEOS = [
  {
    id: 'ZzR1_21YCIk',
    title: 'Money Matters Forum',
    desc: 'Biblical insights, practical stewardship and empowering financial wisdom from the Money Matters Forum.',
  },
  {
    id: '_vCLCdLfwMg',
    title: 'Faith & Finances',
    desc: 'Practical, biblical wisdom for the money decisions you make every day.',
  },
  {
    id: '_OSJLkOH_uM',
    title: 'Stewardship & Freedom',
    desc: 'Moving from financial pressure to peace, generosity and lasting freedom.',
  },
];

const PROGRAMS = [
  {
    icon: <FaSeedling aria-hidden="true" />,
    title: 'Fit for Life',
    desc: 'Workshops, mentorship and coaching that build real business and personal finance skills.',
    to: '/fitforlife',
    cta: 'Explore Fit for Life',
  },
  {
    icon: <FaHandHoldingUsd aria-hidden="true" />,
    title: 'City Finance SACCO',
    desc: 'A saving culture, community lending and accountability for families and small businesses.',
    to: '/ConnectionCardLin',
    cta: 'Connect to the SACCO',
  },
  {
    icon: <FaBookReader aria-hidden="true" />,
    title: 'Recommended Reading',
    desc: 'A curated library of books on stewardship, generosity and building wisely.',
    to: '/bookLib',
    cta: 'Browse the library',
  },
];

const RESOURCES = [
  { icon: <FaBookOpen aria-hidden="true" />, title: 'Articles & Devotionals', desc: 'Short, practical reads that connect Scripture to real financial decisions.' },
  { icon: <FaHandHoldingUsd aria-hidden="true" />, title: 'Financial Guidance', desc: 'Budgeting, saving, debt and giving. Frameworks you can use this week.' },
  { icon: <FaFilePdf aria-hidden="true" />, title: 'Downloadable Guides', desc: 'Worksheets and planners to help you take the next step with confidence.' },
];

const FAQS = [
  {
    q: 'What is the Money Matters ministry?',
    a: 'Money Matters helps our church family grow in financial wisdom from a biblical perspective, through teaching, practical tools, mentorship and community.',
  },
  {
    q: 'Do I need any financial background to join?',
    a: 'Not at all. Whether you are just starting out, rebuilding, or wanting to grow, there is a place for you. We meet you exactly where you are.',
  },
  {
    q: 'How do the teachings approach money?',
    a: 'We start from the conviction that "the earth is the LORD’s, and everything in it" (Psalm 24:1). Money is a tool for ministry and family stability that calls for faithful, disciplined stewardship.',
  },
  {
    q: 'How can I get involved?',
    a: 'Watch a teaching below, explore Fit for Life or the City Finance SACCO, or reach out through our contact page. We would love to walk with you.',
  },
];

const LEADERS = [
  {
    name: 'Pastor Judith Hellen Icumar',
    role: 'Lead, The City SACCO',
    image: pastorJudithImg,
    bio: 'Passionate about empowering families through financial literacy, Pastor Judith leads The City SACCO with a vision to equip members with practical, faith-rooted financial skills.',
  },
  {
    name: 'Mrs Rebecca Baguma',
    role: 'Lead, Fit for Life Program',
    image: bAgumaImg,
    bio: 'An advocate for holistic development, Mrs Rebecca guides members through workshops on business planning, budgeting and entrepreneurship toward financial independence.',
  },
];

function VideoCard({ video }) {
  const [playing, setPlaying] = useState(false);
  const thumb = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;

  return (
    <article className="mm-video tcc-card tcc-card--hover">
      <div className="mm-video__frame">
        {playing ? (
          <iframe
            className="mm-video__iframe"
            src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0`}
            title={video.title}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            className="mm-video__facade"
            onClick={() => setPlaying(true)}
            aria-label={`Play video: ${video.title}`}
          >
            <img src={thumb} alt="" className="mm-video__thumb" loading="lazy" />
            <span className="mm-video__play"><FaPlay aria-hidden="true" /></span>
          </button>
        )}
      </div>
      <div className="mm-video__body">
        <h3 className="mm-video__title">{video.title}</h3>
        <p className="mm-video__desc">{video.desc}</p>
        <a
          className="mm-video__link"
          href={`https://youtu.be/${video.id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaYoutube aria-hidden="true" /> Watch on YouTube
        </a>
      </div>
    </article>
  );
}

function Financial() {
  return (
    <>
      {/* HERO */}
      <header className="mm-hero">
        <div className="tcc-container mm-hero__inner">
          <div className="mm-hero__copy tcc-rise">
            <p className="tcc-eyebrow mm-hero__eyebrow">Money Matters Ministry</p>
            <h1 className="mm-hero__title">Financial wisdom, rooted in faith</h1>
            <p className="mm-hero__lead">
              Money Matters helps you grow in stewardship, generosity and freedom,
              turning biblical principles into practical steps for your everyday life,
              family and future.
            </p>
            <div className="mm-hero__actions">
              <a
                className="tcc-btn tcc-btn--accent tcc-btn--lg"
                href={`https://youtu.be/${VIDEOS[0].id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaPlay aria-hidden="true" /> Watch Latest Teaching
              </a>
              <a className="tcc-btn tcc-btn--outline-on-dark tcc-btn--lg" href="#mm-resources">
                Explore Resources
              </a>
            </div>
          </div>
          <div className="mm-hero__media">
            <img src={trainingImg} alt="Members learning together at a Money Matters financial training" />
            <span className="mm-hero__badge"><FaRegLightbulb aria-hidden="true" /> Biblical stewardship, made practical</span>
          </div>
        </div>
      </header>

      {/* ABOUT ROWENA TURINAWE */}
      <section className="tcc-section mm-about" aria-labelledby="mm-about-heading">
        <div className="tcc-container mm-about__grid">
          <div className="mm-about__portrait">
            {ROWENA.image ? (
              <img src={ROWENA.image} alt={`Portrait of ${ROWENA.name}`} className="mm-about__photo" />
            ) : (
              <div className="mm-about__monogram" role="img" aria-label={`${ROWENA.name} portrait placeholder`}>
                <span>R</span>
              </div>
            )}
            <div className="mm-about__namecard">
              <p className="mm-about__name">{ROWENA.name}</p>
              <p className="mm-about__role">{ROWENA.role}</p>
            </div>
          </div>
          <div className="mm-about__text">
            <p className="tcc-eyebrow">Meet your coordinator</p>
            <h2 className="tcc-h2" id="mm-about-heading">Coordinated with heart by {ROWENA.name}</h2>
            <p className="mm-about__intro">{ROWENA.intro}</p>
            <p className="mm-about__bio">{ROWENA.bio}</p>
            <blockquote className="mm-about__quote">
              <FaQuoteLeft aria-hidden="true" />
              <span>{ROWENA.quote}</span>
            </blockquote>

            <div className="mm-vision-card">
              <img src={VISION.image} alt={`Portrait of ${VISION.name}`} className="mm-vision-card__img" loading="lazy" />
              <div className="mm-vision-card__body">
                <p className="mm-vision-card__label">Vision bearer</p>
                <p className="mm-vision-card__name">{VISION.name}</p>
                <p className="mm-vision-card__role">The City Church</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED TEACHINGS */}
      <section className="tcc-section tcc-section--surface2 mm-videos" aria-labelledby="mm-videos-heading">
        <div className="tcc-container">
          <div className="mm-section-head">
            <p className="tcc-eyebrow">Featured teachings</p>
            <h2 className="tcc-h2" id="mm-videos-heading">Watch &amp; grow</h2>
            <p className="tcc-lead">
              Start with these teachings on faith, money and freedom. Tap play to watch
              right here, or open them on YouTube.
            </p>
          </div>
          <div className="mm-videos__grid">
            {VIDEOS.map((v) => <VideoCard key={v.id} video={v} />)}
          </div>
        </div>
      </section>

      {/* PROGRAMS + RESOURCES */}
      <section className="tcc-section mm-resources" id="mm-resources" aria-labelledby="mm-resources-heading">
        <div className="tcc-container">
          <div className="mm-section-head">
            <p className="tcc-eyebrow">Learning resources</p>
            <h2 className="tcc-h2" id="mm-resources-heading">Keep going, together</h2>
            <p className="tcc-lead">Programs, guidance and tools to help you put what you learn into practice.</p>
          </div>

          <div className="mm-programs">
            {PROGRAMS.map((p) => (
              <article className="mm-program tcc-card tcc-card--hover" key={p.title}>
                <span className="mm-program__icon">{p.icon}</span>
                <h3 className="mm-program__title">{p.title}</h3>
                <p className="mm-program__desc">{p.desc}</p>
                <Link to={p.to} className="mm-program__cta">
                  {p.cta} <FaArrowRight aria-hidden="true" />
                </Link>
              </article>
            ))}
          </div>

          <div className="mm-reslist">
            {RESOURCES.map((r) => (
              <div className="mm-res tcc-card" key={r.title}>
                <span className="mm-res__icon">{r.icon}</span>
                <div>
                  <h3 className="mm-res__title">{r.title}</h3>
                  <p className="mm-res__desc">{r.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="tcc-section tcc-section--surface2 mm-faq" aria-labelledby="mm-faq-heading">
        <div className="tcc-container--narrow tcc-container">
          <div className="mm-section-head">
            <p className="tcc-eyebrow">Good to know</p>
            <h2 className="tcc-h2" id="mm-faq-heading">Frequently asked questions</h2>
          </div>
          <div className="mm-faq__list">
            {FAQS.map((f) => (
              <details className="mm-faq__item" key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* MINISTRY TEAM */}
      <section className="tcc-section mm-team" aria-labelledby="mm-team-heading">
        <div className="tcc-container">
          <div className="mm-section-head">
            <p className="tcc-eyebrow">The team</p>
            <h2 className="tcc-h2" id="mm-team-heading">Serving alongside you</h2>
          </div>
          <div className="mm-team__grid">
            {LEADERS.map((l) => (
              <article className="mm-leader tcc-card" key={l.name}>
                <img src={l.image} alt={`Portrait of ${l.name}`} className="mm-leader__img" loading="lazy" />
                <div className="mm-leader__body">
                  <h3 className="mm-leader__name">{l.name}</h3>
                  <p className="mm-leader__role">{l.role}</p>
                  <p className="mm-leader__bio">{l.bio}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mm-cta" aria-labelledby="mm-cta-heading">
        <div className="tcc-container mm-cta__inner">
          <h2 className="mm-cta__title" id="mm-cta-heading">Take your next step toward financial peace</h2>
          <p className="mm-cta__text">
            Watch a teaching, join an upcoming session, or connect with the Money Matters
            team. We would be honoured to walk with you.
          </p>
          <div className="mm-cta__actions">
            <a className="tcc-btn tcc-btn--on-dark tcc-btn--lg" href={`https://youtu.be/${VIDEOS[0].id}`} target="_blank" rel="noopener noreferrer">
              <FaPlay aria-hidden="true" /> Watch a Teaching
            </a>
            <Link className="tcc-btn tcc-btn--outline-on-dark tcc-btn--lg" to="/contact-us">
              Connect With Us
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

export default Financial;
